import { useState } from 'react';
import { WifiOff, Wifi, Clock, CheckCircle } from 'lucide-react';
import { useOffline } from '@/hooks/useOffline';

export function OfflineIndicator() {
  const { isOnline, pendingActions, lastSync } = useOffline();
  const [showDetails, setShowDetails] = useState(false);

  if (isOnline && pendingActions === 0) {
    return null; // Don't show anything when online and no pending actions
  }

  return (
    <>
      {/* Offline Banner */}
      <div className={`fixed top-0 left-0 right-0 z-50 ${
        isOnline ? 'bg-green-600' : 'bg-red-600'
      } text-white px-4 py-2 text-sm`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center">
            {isOnline ? (
              <Wifi className="w-4 h-4 mr-2" />
            ) : (
              <WifiOff className="w-4 h-4 mr-2" />
            )}
            <span>
              {isOnline 
                ? pendingActions > 0 
                  ? `Back online - Syncing ${pendingActions} pending actions...`
                  : 'Connected'
                : 'You\'re offline - Some features may be limited'
              }
            </span>
          </div>
          
          {(pendingActions > 0 || !isOnline) && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-white hover:text-gray-200 underline"
            >
              Details
            </button>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Connection Status
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Connection Status */}
              <div className="flex items-center">
                {isOnline ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                ) : (
                  <WifiOff className="w-5 h-5 text-red-600 mr-3" />
                )}
                <div>
                  <p className="font-medium text-gray-900">
                    {isOnline ? 'Online' : 'Offline'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {isOnline 
                      ? 'All features are available'
                      : 'Limited functionality - changes will sync when reconnected'
                    }
                  </p>
                </div>
              </div>

              {/* Pending Actions */}
              {pendingActions > 0 && (
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-orange-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {pendingActions} Pending Actions
                    </p>
                    <p className="text-sm text-gray-600">
                      Messages, quotes, and updates waiting to sync
                    </p>
                  </div>
                </div>
              )}

              {/* Last Sync */}
              {lastSync && (
                <div className="text-sm text-gray-600">
                  <p>Last synced: {lastSync.toLocaleString()}</p>
                </div>
              )}

              {/* Offline Features */}
              {!isOnline && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Available Offline:
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• View cached jobs and messages</li>
                    <li>• Draft messages (will send when online)</li>
                    <li>• Browse your profile and settings</li>
                    <li>• View saved addresses and templates</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowDetails(false)}
                className="btn btn-primary w-full"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}